import type { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import type { ClipboardLocalizationSnapshot } from "./copyPasteClipboard";

export const LOCALIZATION_KEY_PREFIX = "key_";

export function collectReferencedLocalizationKeys(value: unknown): string[] {
  const keys = new Set<string>();
  const visited = new Set<object>();

  const visit = (current: unknown) => {
    if (typeof current === "string") {
      if (current.startsWith(LOCALIZATION_KEY_PREFIX)) keys.add(current);
      return;
    }
    if (current === null || typeof current !== "object") return;
    if (visited.has(current)) return;
    visited.add(current);

    if (Array.isArray(current)) {
      current.forEach(visit);
      return;
    }

    Object.values(current).forEach(visit);
  };

  visit(value);
  return [...keys];
}

export function buildClipboardLocalizationSnapshot(
  value: unknown,
  localizedEntries: LocalizedStringRecord,
  sourceReferenceLanguage: string,
  sourceCurrentLanguage: string,
): ClipboardLocalizationSnapshot | undefined {
  const referencedKeys = collectReferencedLocalizationKeys(value);
  if (referencedKeys.length === 0) return undefined;

  const entries: ClipboardLocalizationSnapshot["entries"] = {};
  referencedKeys.forEach((key) => {
    const sourceEntry = localizedEntries[key];
    if (!sourceEntry) return;

    entries[key] = Object.fromEntries(
      Object.entries(sourceEntry).map(([shortcode, localizedValue]) => [
        shortcode,
        { ...localizedValue },
      ]),
    );
  });

  return {
    sourceReferenceLanguage,
    sourceCurrentLanguage,
    entries,
  };
}

export interface ClipboardLocalizationDiagnostics {
  missingKeys: string[];
  hasNoSharedLanguage: boolean;
}

export function getClipboardLocalizationDiagnostics(
  value: unknown,
  snapshot: ClipboardLocalizationSnapshot | undefined,
  destinationLanguageShortcodes: string[],
): ClipboardLocalizationDiagnostics {
  const referencedKeys = collectReferencedLocalizationKeys(value);
  const missingKeys = referencedKeys.filter(
    (key) => snapshot?.entries[key] === undefined,
  );
  const destinationLanguages = new Set(destinationLanguageShortcodes);
  const capturedEntries = referencedKeys
    .map((key) => snapshot?.entries[key])
    .filter(
      (entry): entry is NonNullable<typeof entry> => entry !== undefined,
    );
  const hasSharedLanguage = capturedEntries.some((entry) =>
    Object.keys(entry).some((shortcode) =>
      destinationLanguages.has(shortcode),
    ),
  );

  return {
    missingKeys,
    hasNoSharedLanguage:
      referencedKeys.length > 0 &&
      capturedEntries.length > 0 &&
      !hasSharedLanguage,
  };
}
